import { useQuery } from "@tanstack/react-query";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import {
  InvocationResponseSchema,
  type InvocationResponse,
  type RunProgress,
} from "../types";
import { SeverityTier } from "../types/severity-tier";

/** Map invocation envelope status (includes TERMINATED) to RunProgress status union. */
function mapInvocationStatus(
  raw: InvocationResponse["status"],
): RunProgress["status"] {
  const lower = raw.toLowerCase();
  if (lower === "terminated") return "failed";
  return lower as RunProgress["status"];
}

function transformInvocationToProgress(
  response: InvocationResponse,
): RunProgress {
  const result = response.result;
  const envelopeStatus = mapInvocationStatus(response.status);

  if (!result) {
    return {
      workflow_id: response.workflow_id,
      status: envelopeStatus,
      total_packets: 0,
      processed_packets: 0,
      match_rate: 0,
      status_breakdown: [],
      error_rate: 0,
      circuit_breaker_state: "SAFE",
      // TODO: InvocationResult has no shard data — EnterpriseProgress needs API support (#184)
      shard_summaries: null,
      failed_workflow_ids: [],
      batch_id: null,
      started_at: null,
      completed_at: null,
    };
  }

  const processed =
    result.match_count +
    result.acceptable_count +
    result.minor_count +
    result.variance_count +
    result.critical_count +
    result.error_count;

  const total = result.total_packets;

  const breakdownEntries = [
    { tier: SeverityTier.MATCH, count: result.match_count },
    { tier: SeverityTier.ACCEPTABLE, count: result.acceptable_count },
    { tier: SeverityTier.MINOR, count: result.minor_count },
    { tier: SeverityTier.VARIANCE, count: result.variance_count },
    { tier: SeverityTier.CRITICAL, count: result.critical_count },
    { tier: SeverityTier.ERROR, count: result.error_count },
    { tier: SeverityTier.SKIPPED_ZERO, count: result.skipped_count },
  ];

  const status_breakdown = breakdownEntries
    .filter((e) => e.count > 0)
    .map((e) => ({
      tier: e.tier,
      count: e.count,
      percentage: total > 0 ? (e.count / total) * 100 : 0,
    }));

  const errorRate = total > 0 ? (result.error_count / total) * 100 : 0;

  let started_at: string | null = null;
  if (result.completed_at && result.processing_duration_seconds > 0) {
    const completedDate = new Date(result.completed_at);
    started_at = new Date(
      completedDate.getTime() - result.processing_duration_seconds * 1000,
    ).toISOString();
  }

  return {
    workflow_id: response.workflow_id,
    status: result.status,
    total_packets: total,
    processed_packets: processed,
    match_rate: result.overall_match_rate,
    status_breakdown,
    error_rate: errorRate,
    circuit_breaker_state: "SAFE",
    // TODO: InvocationResult has no shard data — EnterpriseProgress needs API support (#184)
    shard_summaries: null,
    failed_workflow_ids: [],
    batch_id: result.batch_id,
    started_at,
    completed_at: result.completed_at,
    // Extended fields
    message: result.message ?? null,
    directory_path: result.directory_path ?? null,
    org_category: result.org_category ?? null,
    org_id: result.org_id ?? null,
    completion_rate: result.overall_completion_rate ?? 0,
    completed_match_rate: result.completed_match_rate ?? 0,
    skipped_count: result.skipped_count,
    dlq_count: result.dlq_count ?? 0,
    processing_duration_seconds: result.processing_duration_seconds,
  };
}

export function useShadowRunProgress(agentId: string, workflowId: string) {
  const query = useQuery({
    queryKey: ["shadowRunProgress", agentId, workflowId],
    queryFn: async () => {
      const instance = await getCoreAxiosInstance();
      const { data } = await instance.get(
        `/api/v1/agents/${agentId}/invocations/${workflowId}`,
      );
      const envelope = InvocationResponseSchema.parse(data);
      return transformInvocationToProgress(envelope);
    },
    enabled: !!agentId && !!workflowId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === "completed" ||
        status === "failed"
      ) {
        return false;
      }
      return 2000;
    },
  });

  return query;
}
